<script setup>
// CampaignFinish.vue
import { onMounted, onUnmounted, computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import PageHeader from 'dashboard/routes/dashboard/settings/SettingsSubPageHeader.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { v4 as uuidv4 } from 'uuid';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { toast } from 'dashboard/helper/toast';
import AiCampaignTargetsAPI from 'dashboard/api/aiCampaignTargets';
import OpenConversationModeField from './OpenConversationModeField.vue';
import { buildOpenConversationKeys } from './releaseWindow';
import { ArrowLeft, ArrowRight, Eye, Sparkles } from 'lucide-vue-next';

// Import shadcn/ui Tabs components
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-shadcn/new-york/ui/tabs';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

// Estado para o nome da campanha
const campaignName = ref('');
const labels = useMapGetter('labels/getLabels');

const { currentUser } = store.state.auth;

// Estado para o modal de processamento
const isProcessing = ref(false);
const progressMessage = ref('');
const currentProgress = ref(0);
const totalCampaigns = ref(0);

// Tab state
const activeTab = ref('details');
const tabs = [
  {
    id: 'details',
    label: 'Detalhes da Campanha',
    shortLabel: 'Detalhes',
    icon: '📝',
    title: 'Detalhes da Campanha',
    description: 'Defina o nome e tag para sua campanha',
  },
  {
    id: 'summary',
    label: 'Resumo Completo',
    shortLabel: 'Resumo',
    icon: '📋',
    title: 'Resumo da Campanha',
    description: 'Revise todas as configurações antes de criar',
  },
];

// Estado para armazenar os dados da campanha
const campaignData = computed(() => store.getters['campaigns/getCampaignData']);
const selectedTag = ref(campaignData.value?.selectedTag || 0);

// Destino da resposta da IA: para onde entra quem responder a campanha.
// 'default' = entrada padrão do fluxo (triagem da recepção). As demais opções
// vêm do backend (saídas do Roteamento + fluxos de campanha).
const DEFAULT_DESTINATION = 'default';
const selectedDestination = ref(
  campaignData.value?.selectedDestination || DEFAULT_DESTINATION
);
const destinationTargets = ref([]);
// Só exibimos o seletor quando o inbox tem IA em produção com fluxo publicado.
const destinationAvailable = ref(false);

const encodeTargetValue = target => {
  if (target.type === 'routing_exit') {
    return `routing_exit:${target.tool_name}`;
  }
  if (target.type === 'campaign_flow') {
    return `campaign_flow:${target.flow_key}`;
  }
  return DEFAULT_DESTINATION;
};

const groupPrefix = target => {
  if (target.type === 'routing_exit') {
    return t('CAMPAIGN.WHATSAPP_OFFICIAL.FINISH.REPLY_DESTINATION.GROUP_ROUTE');
  }
  if (target.type === 'campaign_flow') {
    return t(
      'CAMPAIGN.WHATSAPP_OFFICIAL.FINISH.REPLY_DESTINATION.GROUP_CAMPAIGN_FLOW'
    );
  }
  return '';
};

// ComboBox é lista plana (só value/label), então diferenciamos os grupos no
// próprio label via prefixo i18n e anexamos a descrição do target como contexto.
const destinationOptions = computed(() => {
  const base = [
    {
      value: DEFAULT_DESTINATION,
      label: t('CAMPAIGN.WHATSAPP_OFFICIAL.FINISH.REPLY_DESTINATION.DEFAULT'),
    },
  ];
  const targetOptions = destinationTargets.value.map(target => {
    const prefix = groupPrefix(target);
    const label = target.description
      ? `${prefix} ${target.label} — ${target.description}`
      : `${prefix} ${target.label}`;
    return { value: encodeTargetValue(target), label };
  });
  return [...base, ...targetOptions];
});

// Decodifica o value selecionado no contrato que o create da campanha espera.
// 'default' → null (a chave campaign_entry é omitida do payload).
const buildCampaignEntry = () => {
  const value = selectedDestination.value;
  if (!value || value === DEFAULT_DESTINATION) return null;

  const [type, ...rest] = value.split(':');
  const key = rest.join(':');
  if (type === 'routing_exit') {
    return { destination_type: 'routing_exit', tool_name: key };
  }
  if (type === 'campaign_flow') {
    return { destination_type: 'campaign_flow', campaign_flow_key: key };
  }
  return null;
};

// "Se o contato já tem conversa aberta": modo (send/new/hold) + janela semanal
// de release (só no hold). Default 'hold' + janela padrão — omitidos do payload
// (paridade com as campanhas atuais). Ver Campaigns::ReleaseWindow.
const openConversationMode = ref(
  campaignData.value?.openConversationMode || 'hold'
);
const releaseWindow = ref(campaignData.value?.releaseWindow || null);

// Tab validation
const tabValidation = computed(() => ({
  details: !!campaignName.value,
  summary: true,
}));

// Tab navigation helpers
const canNavigateToTab = tabId => {
  const tabIndex = tabs.findIndex(tab => tab.id === tabId);
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);

  if (tabIndex <= currentIndex) return true;

  // Check if previous tabs are valid
  for (let i = 0; i < tabIndex; i++) {
    const tab = tabs[i];
    if (!tabValidation.value[tab.id]) {
      return false;
    }
  }
  return true;
};

const onTabChange = async tabId => {
  if (!canNavigateToTab(tabId)) return;
  activeTab.value = tabId;
};

const nextTab = async () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex < tabs.length - 1) {
    const nextTabId = tabs[currentIndex + 1].id;
    await onTabChange(nextTabId);
  }
};

const previousTab = async () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex > 0) {
    const prevTabId = tabs[currentIndex - 1].id;
    activeTab.value = prevTabId;
  }
};

// Get current tab data
const currentTabData = computed(() => {
  return tabs.find(tab => tab.id === activeTab.value);
});

// Função para formatar opções do ComboBox
const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const tagsList = computed(() => [
  { value: 0, label: 'Nenhuma tag' },
  ...mapToOptions(labels.value || [], 'id', 'title'),
]);

// Função para formatar datas para o padrão brasileiro (DD/MM/AAAA).
// ATENÇÃO: NÃO é a formatDate do campaignNodeSummary — esta parseia com
// `new Date()`, que desloca 'aaaa-mm-dd' um dia pra trás em fuso UTC-negativo
// (o input type="date" grava 'aaaa-mm-dd'). Divergência conhecida; unificar
// com o módulo muda a data exibida, então fica registrada em vez de trocada
// em silêncio.
const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Função para traduzir o tipo de recorrência
const translateRecurrenceType = recurrenceType => {
  const recurrenceMap = {
    daily: 'Diária',
    weekly: 'Semanal',
    monthly: 'Mensal',
    custom: 'Personalizada',
  };
  return recurrenceMap[recurrenceType] || recurrenceType;
};

// Função para traduzir operadores de filtro
const translateOperator = operator => {
  const operatorMap = {
    equal_to: 'é igual a',
    not_equal_to: 'é diferente de',
    contains: 'contém',
    does_not_contain: 'não contém',
    is_greater_than: 'é maior que',
    is_greater_than_or_equal: 'é maior ou igual a',
    is_less_than: 'é menor que',
    is_less_than_or_equal: 'é menor ou igual a',
    starts_with: 'começa com',
    ends_with: 'termina com',
  };
  return operatorMap[operator] || operator;
};

// Computeds para dados de resumo
const summaryData = computed(() => {
  const data = campaignData.value;
  if (!data) return null;

  return {
    table: {
      selectedTable: data.selectedTable || 'Não selecionada',
      phoneColumn: data.selectedPhoneColumn || 'Não selecionada',
      contactColumn: data.selectedContactColumn || 'Não selecionada',
    },
    placeholders: data.placeholders || {},
    filters: data.filters || [],
    schedule: data.schedule || {},
    template: data.template || {},
  };
});

const prepareCampaignDetails = async () => {
  await store.dispatch('campaigns/saveCampaignData', {
    campaignName: campaignName.value,
    selectedTag: selectedTag.value,
    selectedDestination: selectedDestination.value,
    openConversationMode: openConversationMode.value,
    releaseWindow: releaseWindow.value,
  });

  const updatedCampaignData = store.getters['campaigns/getCampaignData'];

  // Destino da resposta da IA (omitido quando é a entrada padrão do fluxo).
  const campaignEntry = buildCampaignEntry();
  // Modo p/ contato em atendimento + janela (omitidos quando == default).
  const openConversationKeys = buildOpenConversationKeys(
    openConversationMode.value,
    releaseWindow.value
  );

  const filters = (updatedCampaignData.filters || []).map(filterItem => ({
    attribute_key: filterItem.attributeKey,
    values: filterItem.values,
    filter_operator: filterItem.filterOperator,
    query_operator: filterItem.queryOperator,
    group_index: filterItem.groupIndex,
    attribute_model: filterItem.attributeModel,
  }));

  // Verifica se já existe filterData salvo (ex: Google Sheets)
  const existingFilterData = updatedCampaignData.filterData;
  const isGoogleSheets =
    existingFilterData?.source_type === 'google_sheets' ||
    existingFilterData?.spreadsheet_id;

  // Filter data para o schedule
  let filterData;

  if (isGoogleSheets && existingFilterData) {
    // Para Google Sheets, usa o filterData já configurado e adiciona label_id
    filterData = {
      ...existingFilterData,
      send_message: {
        ...existingFilterData.send_message,
        label_id: selectedTag.value,
        ...(campaignEntry ? { campaign_entry: campaignEntry } : {}),
        ...openConversationKeys,
      },
    };
  } else {
    // Para Database, constrói o filterData padrão
    filterData = {
      source_type: 'database',
      tabela: updatedCampaignData.selectedTable,
      placeholders: updatedCampaignData.placeholders,
      send_message: {
        name_column: updatedCampaignData.selectedContactColumn,
        phone_column: updatedCampaignData.selectedPhoneColumn,
        email_column: null,
        label_id: selectedTag.value,
        ...(campaignEntry ? { campaign_entry: campaignEntry } : {}),
        ...openConversationKeys,
      },
      filtros: filters,
    };
  }

  const processedParams =
    updatedCampaignData.template?.components
      ?.filter(
        component =>
          component.type === 'BODY' && component.example?.body_text_named_params
      )
      ?.flatMap(component =>
        component.example.body_text_named_params.map(param => param.param_name)
      )
      ?.reduce((acc, paramName) => {
        acc[paramName] = null;
        return acc;
      }, {}) || {};

  // Template params para o schedule
  const templateParams = {
    name: updatedCampaignData.template?.name,
    category: updatedCampaignData.template?.category,
    language: updatedCampaignData.template?.language,
    namespace: updatedCampaignData.template?.namespace,
    processed_params: processedParams,
  };

  const campaignDetails = {
    // Dados da campanha
    campaign: {
      title: campaignName.value,
      message:
        updatedCampaignData.template?.components?.find(
          comp => comp.type === 'BODY'
        )?.text || '',
      inbox_id: updatedCampaignData.template?.inboxId,
    },
    // Dados para o schedule
    scheduled_dates: updatedCampaignData.schedule?.scheduledDates || [],
    filter_data: filterData,
    templateParams: templateParams,
  };

  return campaignDetails;
};

const createCampaign = async () => {
  if (!campaignName.value) {
    useAlert('Por favor, insira o nome da campanha!');
    return;
  }

  try {
    const campaignDetails = await prepareCampaignDetails();
    const { scheduled_dates, campaign, ...scheduleData } = campaignDetails;

    isProcessing.value = true;
    progressMessage.value = 'Criando campanha...';

    // Payload para criar uma única campanha com múltiplos agendamentos
    const campaignPayload = {
      title: campaign.title,
      message: campaign.message,
      inbox_id: campaign.inbox_id,
      sender_id: currentUser.id,
      campaign_type: 'whatsapp_official',
      scheduled_dates: scheduled_dates,
      filter_data: scheduleData.filter_data,
      templateParams: scheduleData.templateParams,
    };

    await store.dispatch('campaigns/create', campaignPayload);

    progressMessage.value = 'Campanha criada com sucesso!';
    setTimeout(() => {
      isProcessing.value = false;
      router.push({ name: 'campaigns_whatsapp_official_schedule' });
    }, 1500);
  } catch (error) {
    console.error('Erro ao criar campanha:', error);
    isProcessing.value = false;

    let errorMessage = 'Ocorreu um erro ao criar a campanha. Tente novamente.';
    if (error.response?.data?.errors) {
      errorMessage = Array.isArray(error.response.data.errors)
        ? error.response.data.errors.join(', ')
        : error.response.data.errors;
    } else if (error.message) {
      errorMessage = error.message;
    }

    useAlert(errorMessage);
  }
};

// Computed para verificar se pode submeter
const canSubmit = computed(() => {
  return !!campaignName.value;
});

// Watch para preencher automaticamente o nome da campanha com base no template
watch(
  () => campaignData.value?.template?.name,
  newTemplateName => {
    if (newTemplateName && !campaignName.value) {
      campaignName.value = newTemplateName;
    }
  },
  { immediate: true }
);

// Watch para salvar a tag selecionada
watch(selectedTag, newValue => {
  store.dispatch('campaigns/saveCampaignData', {
    selectedTag: newValue,
  });
});

// Watch para salvar o destino da resposta selecionado (sobrevive voltar/avançar)
watch(selectedDestination, newValue => {
  store.dispatch('campaigns/saveCampaignData', {
    selectedDestination: newValue,
  });
});

// Idem para o modo de conversa aberta + janela de release.
watch([openConversationMode, releaseWindow], () => {
  store.dispatch('campaigns/saveCampaignData', {
    openConversationMode: openConversationMode.value,
    releaseWindow: releaseWindow.value,
  });
});

// Busca os destinos de resposta possíveis para o inbox da campanha.
// available:false → seletor oculto (campanha sem IA é caso legítimo).
// Erro de rede/500 → falha visível (toast) e seletor oculto.
onMounted(async () => {
  const inboxId = campaignData.value?.template?.inboxId;
  if (!inboxId) return;

  try {
    const { data } = await AiCampaignTargetsAPI.get({ inboxId });
    if (!data?.available) {
      destinationAvailable.value = false;
      return;
    }
    destinationAvailable.value = true;
    destinationTargets.value = Array.isArray(data.targets) ? data.targets : [];
  } catch (error) {
    destinationAvailable.value = false;
    toast.error(
      t('CAMPAIGN.WHATSAPP_OFFICIAL.FINISH.REPLY_DESTINATION.FETCH_ERROR')
    );
  }
});
</script>

<template>
  <div
    class="border border-woot-400 dark:border-woot-400 bg-n-background min-h-[100px] p-6 w-full min-w-0 flex-1 flex flex-col overflow-y-auto"
  >
    <!-- Modal de Processamento -->
    <div
      v-if="isProcessing"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div
        class="bg-white dark:bg-slate-900 p-8 rounded-lg shadow-2xl flex flex-col items-center max-w-md w-full mx-4"
      >
        <div class="spinner mb-4" />
        <h3 class="text-lg font-semibold text-n-slate-12 dark:text-white mb-2">
          Criando Campanhas
        </h3>
        <p class="text-sm text-n-slate-10 dark:text-n-slate-11 text-center">
          Aguarde enquanto sua campanha é criada...
        </p>
      </div>
    </div>

    <!-- Header -->
    <div class="page-header-container mb-4 sm:mb-6 flex-shrink-0">
      <PageHeader
        class="w-full max-w-4xl"
        header-title="Revisar e Finalizar Campanha"
        header-content="Revise todos os detalhes da sua campanha antes de criar. Você poderá acompanhar o progresso após a criação."
      />
    </div>

    <!-- Main Content with Tabs -->
    <div class="w-full flex flex-col">
      <Tabs
        :model-value="activeTab"
        class="w-full h-full flex flex-col"
        @update:model-value="onTabChange"
      >
        <!-- Tab Navigation -->
        <TabsList class="tab-list">
          <TabsTrigger
            v-for="tab in tabs"
            :key="tab.id"
            :value="tab.id"
            :disabled="!canNavigateToTab(tab.id)"
            class="relative transition-all duration-200 text-xs sm:text-sm font-medium"
            :class="[
              !canNavigateToTab(tab.id) && 'opacity-50 cursor-not-allowed',
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 sm:gap-2 min-w-0"
            >
              <span class="text-sm sm:text-base flex-shrink-0">{{
                tab.icon
              }}</span>
              <span class="hidden sm:block truncate">{{ tab.label }}</span>
              <span class="sm:hidden truncate">{{ tab.shortLabel }}</span>
              <span
                v-if="tabValidation[tab.id]"
                class="text-green-500 dark:text-green-400 ml-1 flex-shrink-0"
              >
                ✓
              </span>
            </div>
          </TabsTrigger>
        </TabsList>

        <!-- Tab Content -->
        <div class="mt-4">
          <!-- Details Tab -->
          <TabsContent value="details" class="space-y-4 sm:space-y-6">
            <div class="modern-tab-container mb-4">
              <!-- Decorative Background Elements -->
              <div class="background-decoration">
                <div class="decoration-blob decoration-blob-1" />
                <div class="decoration-blob decoration-blob-2" />
              </div>

              <!-- Header Section -->
              <div class="tab-header">
                <div class="header-content">
                  <div class="accent-line" />
                  <div class="min-w-0 flex-1">
                    <h3
                      class="text-lg font-semibold text-n-slate-12 dark:text-white mb-2"
                    >
                      {{ currentTabData?.title }}
                    </h3>
                    <p class="description-text">
                      {{ currentTabData?.description }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Main Content Area -->
              <div class="main-content-area">
                <!-- Form Content -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Input
                      v-model="campaignName"
                      label="Nome da Campanha"
                      placeholder="Digite o nome da campanha"
                      :disabled="isProcessing"
                      class="w-full"
                    />
                  </div>
                  <div>
                    <label
                      class="mb-1 text-sm text-n-slate-12 dark:text-slate-50 block"
                      >Selecionar Tag</label>
                    <ComboBox
                      v-model="selectedTag"
                      :options="tagsList"
                      placeholder="Selecione uma tag (opcional)"
                      :disabled="isProcessing"
                      class="w-full"
                    />
                  </div>
                </div>

                <!-- Destino da resposta da IA (só quando o inbox tem IA em produção) -->
                <div v-if="destinationAvailable" class="mt-6">
                  <label
                    class="mb-1 text-sm text-n-slate-12 dark:text-slate-50 block"
                  >
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP_OFFICIAL.FINISH.REPLY_DESTINATION.LABEL'
                      )
                    }}
                  </label>
                  <ComboBox
                    v-model="selectedDestination"
                    :options="destinationOptions"
                    :disabled="isProcessing"
                    class="w-full"
                  />
                  <p class="mt-1 text-xs text-n-slate-10">
                    {{
                      t(
                        'CAMPAIGN.WHATSAPP_OFFICIAL.FINISH.REPLY_DESTINATION.HINT'
                      )
                    }}
                  </p>
                </div>

                <!-- Se o contato já tem conversa aberta (send/new/hold + janela) -->
                <div class="mt-6">
                  <label
                    class="mb-1 text-sm text-n-slate-12 dark:text-slate-50 block"
                  >
                    {{
                      t('CAMPAIGN.WHATSAPP_OFFICIAL.OPEN_CONVERSATION.TITLE')
                    }}
                  </label>
                  <p class="mb-2 text-xs text-n-slate-10">
                    {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.OPEN_CONVERSATION.HINT') }}
                  </p>
                  <OpenConversationModeField
                    v-model:mode="openConversationMode"
                    v-model:release-window="releaseWindow"
                    :disabled="isProcessing"
                  />
                </div>
              </div>

              <!-- Action Bar -->
              <div class="action-bar">
                <div class="action-bar-background" />

                <!-- Back Button -->
                <div class="back-section">
                  <button
                    type="button"
                    :disabled="isProcessing"
                    class="modern-button back-button"
                    @click="router.go(-1)"
                  >
                    <div class="button-shine" />
                    <ArrowLeft class="button-icon" />
                    <span class="hidden sm:inline">Retornar para Agendamento</span>
                    <span class="sm:hidden">Retornar</span>
                  </button>
                </div>

                <!-- Center Actions -->
                <div class="center-actions" />

                <!-- Primary Actions -->
                <div class="primary-actions">
                  <button
                    type="button"
                    :disabled="!tabValidation.details"
                    class="modern-button primary-button"
                    @click="nextTab"
                  >
                    <div class="primary-gradient" />
                    <div class="primary-glow" />
                    <div class="button-content">
                      <span class="button-text">
                        <span class="hidden sm:inline">Próximo: Ver Resumo</span>
                        <span class="sm:hidden">Próximo</span>
                        <ArrowRight class="button-icon ml-2" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>

          <!-- Summary Tab -->
          <TabsContent value="summary" class="h-full flex flex-col">
            <div class="modern-tab-container mb-4">
              <!-- Decorative Background Elements -->
              <div class="background-decoration">
                <div class="decoration-blob decoration-blob-1" />
                <div class="decoration-blob decoration-blob-2" />
              </div>

              <!-- Header Section -->
              <div class="tab-header">
                <div class="header-content">
                  <div class="accent-line" />
                  <div class="min-w-0 flex-1">
                    <h3
                      class="text-lg font-semibold text-n-slate-12 dark:text-white mb-2"
                    >
                      {{ currentTabData?.title }}
                    </h3>
                    <p class="description-text">
                      {{ currentTabData?.description }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Main Content Area with Summary -->
              <div
                class="main-content-area flex-1 overflow-y-auto custom-scrollbar"
              >
                <!-- Summary sections with scroll -->
                <div class="space-y-6 pb-4">
                  <!-- Resumo: Configuração do Público -->
                  <div
                    class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                  >
                    <div
                      class="flex items-center gap-3 pb-4 border-b border-woot-400 mb-4"
                    >
                      <div
                        class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <span class="text-lg">📊</span>
                      </div>
                      <div>
                        <h3
                          class="text-lg font-semibold text-n-slate-12 dark:text-white"
                        >
                          Configuração do Público
                        </h3>
                        <p class="text-sm text-n-slate-10 dark:text-n-slate-11">
                          Tabela e colunas selecionadas
                        </p>
                      </div>
                    </div>

                    <div class="space-y-3">
                      <div class="flex flex-wrap items-center gap-2 text-sm">
                        <span class="text-n-slate-10 dark:text-n-slate-11">Tabela selecionada:</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                        >
                          {{ summaryData?.table.selectedTable }}
                        </span>
                      </div>

                      <div class="flex flex-wrap items-center gap-2 text-sm">
                        <span class="text-n-slate-10 dark:text-n-slate-11">Coluna de telefone:</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                        >
                          {{ summaryData?.table.phoneColumn }}
                        </span>
                      </div>

                      <div class="flex flex-wrap items-center gap-2 text-sm">
                        <span class="text-n-slate-10 dark:text-n-slate-11">Coluna de contato:</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                        >
                          {{ summaryData?.table.contactColumn }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Resumo: Placeholders -->
                  <div
                    class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                  >
                    <div
                      class="flex items-center gap-3 pb-4 border-b border-woot-400 mb-4"
                    >
                      <div
                        class="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <span class="text-lg">🔗</span>
                      </div>
                      <div>
                        <h3
                          class="text-lg font-semibold text-n-slate-12 dark:text-white"
                        >
                          Mapeamento de Placeholders
                        </h3>
                        <p class="text-sm text-n-slate-10 dark:text-n-slate-11">
                          Conexão entre template e dados
                        </p>
                      </div>
                    </div>

                    <div
                      v-if="
                        Object.keys(summaryData?.placeholders || {}).length > 0
                      "
                      class="space-y-3"
                    >
                      <div
                        v-for="[placeholder, column] in Object.entries(
                          summaryData?.placeholders || {}
                        )"
                        :key="placeholder"
                        class="flex flex-wrap items-center gap-2 text-sm"
                      >
                        <span class="text-n-slate-10 dark:text-n-slate-11">Placeholder</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-mono"
                        >
                          {{ placeholder }}
                        </span>
                        <span class="text-n-slate-10 dark:text-n-slate-11">→</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                        >
                          {{ column }}
                        </span>
                      </div>
                    </div>
                    <div
                      v-else
                      class="text-sm text-n-slate-10 dark:text-n-slate-11 italic"
                    >
                      Nenhum placeholder mapeado
                    </div>
                  </div>

                  <!-- Resumo: Filtros -->
                  <div
                    class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                  >
                    <div
                      class="flex items-center gap-3 pb-4 border-b border-woot-400 mb-4"
                    >
                      <div
                        class="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <span class="text-lg">🔍</span>
                      </div>
                      <div>
                        <h3
                          class="text-lg font-semibold text-n-slate-12 dark:text-white"
                        >
                          Filtros Aplicados
                        </h3>
                        <p class="text-sm text-n-slate-10 dark:text-n-slate-11">
                          Critérios de seleção do público
                        </p>
                      </div>
                    </div>

                    <div
                      v-if="
                        summaryData?.filters && summaryData.filters.length > 0
                      "
                      class="space-y-3"
                    >
                      <div
                        v-for="filter in summaryData.filters"
                        :key="filter.id"
                        class="flex flex-wrap items-center gap-2 text-sm"
                      >
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                        >
                          {{ filter.attributeKey }}
                        </span>
                        <span class="text-n-slate-10 dark:text-n-slate-11">{{
                          translateOperator(filter.filterOperator)
                        }}</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300"
                        >
                          {{
                            Array.isArray(filter.values)
                              ? filter.values.join(', ')
                              : filter.values
                          }}
                        </span>
                      </div>
                    </div>
                    <div
                      v-else
                      class="text-sm text-n-slate-10 dark:text-n-slate-11 italic"
                    >
                      Nenhum filtro aplicado - todos os registros serão
                      incluídos
                    </div>
                  </div>

                  <!-- Resumo: Agendamento -->
                  <div
                    class="bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 p-6"
                  >
                    <div
                      class="flex items-center gap-3 pb-4 border-b border-woot-400 mb-4"
                    >
                      <div
                        class="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <span class="text-lg">📅</span>
                      </div>
                      <div>
                        <h3
                          class="text-lg font-semibold text-n-slate-12 dark:text-white"
                        >
                          Agendamento
                        </h3>
                        <p class="text-sm text-n-slate-10 dark:text-n-slate-11">
                          Quando a campanha será executada
                        </p>
                      </div>
                    </div>

                    <div class="space-y-3">
                      <div class="flex flex-wrap items-center gap-2 text-sm">
                        <span class="text-n-slate-10 dark:text-n-slate-11">Período:</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                        >
                          {{ formatDate(summaryData?.schedule.startDate) }}
                        </span>
                        <span class="text-n-slate-10 dark:text-n-slate-11">até</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                        >
                          {{ formatDate(summaryData?.schedule.endDate) }}
                        </span>
                      </div>

                      <div class="flex flex-wrap items-center gap-2 text-sm">
                        <span class="text-n-slate-10 dark:text-n-slate-11">Horário:</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300"
                        >
                          {{ summaryData?.schedule.fromTime }}
                        </span>
                      </div>

                      <div class="flex flex-wrap items-center gap-2 text-sm">
                        <span class="text-n-slate-10 dark:text-n-slate-11">Recorrência:</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300"
                        >
                          {{
                            translateRecurrenceType(
                              summaryData?.schedule.recurrenceType
                            )
                          }}
                        </span>
                      </div>

                      <div
                        v-if="summaryData?.schedule.scheduledDates?.length"
                        class="flex flex-wrap items-center gap-2 text-sm"
                      >
                        <span class="text-n-slate-10 dark:text-n-slate-11">Total de execuções:</span>
                        <span
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-bold"
                        >
                          {{ summaryData.schedule.scheduledDates.length }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Bar -->
              <div class="action-bar">
                <div class="action-bar-background" />

                <!-- Back Button -->
                <div class="back-section">
                  <button
                    type="button"
                    :disabled="isProcessing"
                    class="modern-button back-button"
                    @click="previousTab"
                  >
                    <div class="button-shine" />
                    <ArrowLeft class="button-icon" />
                    <span class="hidden sm:inline">Retornar: Detalhes</span>
                    <span class="sm:hidden">Retornar</span>
                  </button>
                </div>

                <!-- Center Actions -->
                <div class="center-actions" />

                <!-- Primary Actions -->
                <div class="primary-actions">
                  <button
                    type="button"
                    :disabled="!canSubmit || isProcessing"
                    class="modern-button primary-button"
                    @click="createCampaign"
                  >
                    <div class="primary-gradient" />
                    <div class="primary-glow" />
                    <div class="button-content">
                      <span class="button-text">
                        <span class="hidden sm:inline">Criar Campanha</span>
                        <span class="sm:hidden">Criar</span>
                        <Sparkles class="button-icon ml-2" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  </div>
</template>

<style scoped>
.page-header-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.tab-list {
  @apply grid w-full grid-cols-2 p-1 rounded-lg;
  @apply bg-white dark:bg-slate-900;
  @apply border border-woot-400 dark:border-woot-400;
}

/* Prevent horizontal overflow */
* {
  box-sizing: border-box;
}

.min-w-0 {
  min-width: 0;
}

.break-words {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .page-header-container {
    flex-direction: column;
    gap: 12px;
  }
}

/* Spinner animation */
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #16a34a;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Custom scrollbar for summary content */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dark mode scrollbar for summary */
.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: #1e293b;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

/* Firefox scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.dark .custom-scrollbar {
  scrollbar-color: #475569 #1e293b;
}

/* Container Principal */
.modern-tab-container {
  @apply relative min-h-[520px] flex flex-col overflow-hidden rounded-[20px] border border-woot-400 shadow-lg;
  background: linear-gradient(
    135deg,
    rgb(var(--solid-1)) 0%,
    rgb(var(--solid-2)) 50%,
    rgb(var(--solid-1)) 100%
  );
  box-shadow:
    0 20px 40px rgb(var(--slate-4) / 0.1),
    0 1px 3px rgb(var(--slate-6) / 0.1);
}

/* Elementos Decorativos de Fundo */
.background-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.decoration-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.6;
  animation: float 8s ease-in-out infinite;
}

.decoration-blob-1 {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgb(var(--teal-5) / 0.3), transparent);
  top: -50px;
  right: -50px;
  animation-delay: 0s;
}

.decoration-blob-2 {
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgb(var(--iris-5) / 0.25), transparent);
  bottom: -30px;
  left: -30px;
  animation-delay: 4s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) scale(1);
  }
  50% {
    transform: translateY(-20px) scale(1.1);
  }
}

/* Header */
.tab-header {
  position: relative;
  padding: 32px;
  border-bottom: 1px solid transparent;
  background: linear-gradient(
      90deg,
      transparent,
      rgb(var(--teal-6) / 0.3),
      rgb(var(--iris-6) / 0.3),
      transparent
    )
    bottom;
  background-size: 100% 1px;
  background-repeat: no-repeat;
}

.header-content {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.accent-line {
  width: 4px;
  height: 60px;
  border-radius: 2px;
  background: linear-gradient(180deg, rgb(var(--teal-9)), rgb(var(--iris-9)));
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.8;
    transform: scaleY(1);
  }
  50% {
    opacity: 1;
    transform: scaleY(1.1);
  }
}

.description-text {
  font-size: 16px;
  line-height: 1.6;
  font-weight: 500;
  color: rgb(var(--slate-11));
  background: linear-gradient(
    135deg,
    rgb(var(--slate-11)),
    rgb(var(--slate-9))
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  flex: 1;
}

/* Main Content */
.main-content-area {
  flex: 1;
  padding: 24px 32px;
  position: relative;
  z-index: 1;
}

/* Action Bar */
.action-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 32px;
  margin: 0 0 4px 0;
  background: linear-gradient(
    135deg,
    rgb(var(--solid-2) / 0.9),
    rgb(var(--solid-3) / 0.8)
  );
  border-top: 1px solid rgb(var(--teal-6) / 0.3);
  border-radius: 0 0 16px 16px;
  backdrop-filter: blur(10px);
}

.action-bar-background {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      circle at 0% 50%,
      rgb(var(--teal-4) / 0.1),
      transparent 50%
    ),
    radial-gradient(
      circle at 100% 50%,
      rgb(var(--iris-4) / 0.1),
      transparent 50%
    );
  pointer-events: none;
}

.back-section {
  flex-shrink: 0;
}

.center-actions {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.primary-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

/* Base Button Styles */
.modern-button {
  position: relative;
  height: 48px;
  padding: 0 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 140px;
  overflow: hidden;
}

.modern-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Button Shine Effect */
.button-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  transition: transform 0.6s;
  pointer-events: none;
}

.modern-button:hover .button-shine {
  transform: translateX(100%);
}

/* Back Button */
.back-button {
  background: linear-gradient(135deg, rgb(var(--solid-2)), rgb(var(--solid-3)));
  color: rgb(var(--slate-11));
  border: 1px solid rgb(var(--border-weak));
}

.back-button:hover {
  background: linear-gradient(135deg, rgb(var(--solid-3)), rgb(var(--slate-3)));
  border-color: rgb(var(--slate-6) / 0.6);
  transform: translateX(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgb(var(--slate-4) / 0.2);
}

/* Primary Button */
.primary-button {
  position: relative;
  color: white;
  font-weight: 700;
  border: none;
}

.primary-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgb(var(--teal-9)),
    rgb(var(--teal-10)),
    rgb(var(--teal-11))
  );
  border-radius: 12px;
  transition: all 0.3s;
}

.primary-glow {
  position: absolute;
  inset: -4px;
  background: linear-gradient(
    135deg,
    rgb(var(--teal-6) / 0.6),
    rgb(var(--teal-8) / 0.4)
  );
  border-radius: 16px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: -1;
  filter: blur(12px);
}

.button-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.primary-button:hover {
  transform: translateY(-2px) scale(1.05);
}

.primary-button:hover .primary-gradient {
  background: linear-gradient(
    135deg,
    rgb(var(--teal-10)),
    rgb(var(--teal-11)),
    rgb(var(--teal-12))
  );
  animation: gradient-shift 2s ease-in-out infinite;
}

.primary-button:hover .primary-glow {
  opacity: 1;
}

@keyframes gradient-shift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.primary-button:active {
  transform: translateY(0) scale(1.02);
}

/* Button Elements */
.button-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}

.button-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover .button-icon {
  transform: translateX(-2px);
}

.primary-button:hover .button-icon {
  transform: translateX(2px) scale(1.1);
}

/* Responsive */
@media (max-width: 640px) {
  .modern-tab-container {
    min-height: 480px;
    border-radius: 16px;
  }

  .tab-header,
  .main-content-area,
  .action-bar {
    padding: 20px;
  }

  .action-bar {
    flex-direction: column;
    gap: 12px;
  }

  .back-section,
  .primary-actions {
    width: 100%;
  }

  .primary-actions {
    flex-direction: column;
  }

  .modern-button {
    min-width: 100%;
    height: 52px;
    font-size: 16px;
  }

  .description-text {
    font-size: 15px;
  }
}

/* Dark Mode */
@media (prefers-color-scheme: dark) {
  .modern-tab-container {
    background: linear-gradient(
      135deg,
      rgb(var(--solid-1) / 0.95) 0%,
      rgb(var(--solid-2) / 0.9) 50%,
      rgb(var(--solid-1) / 0.95) 100%
    );
    box-shadow:
      0 20px 40px rgb(var(--slate-12) / 0.3),
      0 1px 3px rgb(var(--slate-12) / 0.2);
  }

  .description-text {
    color: rgb(var(--slate-9));
    background: linear-gradient(
      135deg,
      rgb(var(--slate-9)),
      rgb(var(--slate-8))
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .action-bar {
    background: linear-gradient(
      135deg,
      rgb(var(--solid-2) / 0.8),
      rgb(var(--solid-3) / 0.7)
    );
    border-top-color: rgb(var(--teal-7) / 0.4);
  }

  .back-button {
    color: rgb(var(--slate-9));
  }
}

/* Custom styling for tabs to match the design theme */
:deep(.tabs-trigger) {
  transition: all 0.2s ease-in-out;
}

:deep(.tabs-trigger[data-state='active']) {
  background: white;
  border: 1px solid rgb(34 197 94);
  color: rgb(21 128 61);
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
}

:deep(.tabs-trigger[data-state='active']) .dark {
  background: rgb(15 23 42);
  color: rgb(74 222 128);
}
</style>
